// Settings.js - Página de Configurações de Dispositivos
// Smart Home v4.0
// Permite adicionar, editar e remover câmaras e estores via UI

import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon, Camera, Blinds, Plus, Edit2, Trash2,
  Save, X, AlertCircle, CheckCircle, RefreshCw, ArrowLeft,
  Wifi, WifiOff, Server, Eye, EyeOff
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Settings.css';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const Settings = ({ apiUrl, onBack }) => {
  const { t } = useTranslation();
  // Estados gerais
  const [activeTab, setActiveTab] = useState('cameras');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados câmaras
  const [cameras, setCameras] = useState([]);
  const [editingCamera, setEditingCamera] = useState(null);
  const [showCameraForm, setShowCameraForm] = useState(false);
  const [cameraForm, setCameraForm] = useState({
    id: '',
    name: '',
    ip: '',
    stream: 'stream1',
    enabled: true
  });

  // Estados estores
  const [shellys, setShellys] = useState([]);
  const [editingShelly, setEditingShelly] = useState(null);
  const [showShellyForm, setShowShellyForm] = useState(false);
  const [shellyForm, setShellyForm] = useState({
    id: '',
    name: '',
    ip: '',
    room: '',
    type: 'cover',
    enabled: true
  });

  // Estados para teste de conexão
  const [testingConnection, setTestingConnection] = useState({});

  // Estatísticas
  const [stats, setStats] = useState(null);

  // ==================== CARREGAR DADOS ====================

  const loadCameras = async () => {
    try {
      const response = await fetch(`${apiUrl}/config/cameras/all`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCameras(data);
      }
    } catch (err) {
      console.error('Erro ao carregar câmaras:', err);
      setError(t('configuracoes.erroCarregarCamaras'));
    }
  };

  const loadShellys = async () => {
    try {
      const response = await fetch(`${apiUrl}/config/shellys/all`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setShellys(data);
      }
    } catch (err) {
      console.error('Erro ao carregar estores:', err);
      setError(t('configuracoes.erroCarregarEstores'));
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/config/stats`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    setError('');
    await Promise.all([loadCameras(), loadShellys(), loadStats()]);
    setLoading(false);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // ==================== CÂMARAS - CRUD ====================

  const getNextCameraId = async () => {
    try {
      const response = await fetch(`${apiUrl}/config/cameras/next-id`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        return data.next_id;
      }
    } catch (err) {
      // Fallback: calcular localmente
      const maxId = cameras.reduce((max, cam) => Math.max(max, cam.id), 0);
      return maxId + 1;
    }
    return 1;
  };

  const handleNewCamera = async () => {
    const nextId = await getNextCameraId();
    setCameraForm({
      id: nextId,
      name: '',
      ip: '',
      stream: 'stream1',
      enabled: true
    });
    setEditingCamera(null);
    setShowCameraForm(true);
  };

  const handleEditCamera = (camera) => {
    setCameraForm({
      id: camera.id,
      name: camera.name,
      ip: camera.ip,
      stream: camera.stream || 'stream1',
      enabled: camera.enabled
    });
    setEditingCamera(camera.id);
    setShowCameraForm(true);
  };

  const handleSaveCamera = async () => {
    setError('');
    setSuccess('');

    // Validação
    if (!cameraForm.name || !cameraForm.ip) {
      setError(t('configuracoes.nomeIpObrigatorios'));
      return;
    }

    // Validar formato IP
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(cameraForm.ip)) {
      setError(t('configuracoes.ipInvalido'));
      return;
    }

    setLoading(true);

    try {
      const url = editingCamera
        ? `${apiUrl}/config/cameras/${editingCamera}`
        : `${apiUrl}/config/cameras`;
      
      const method = editingCamera ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(cameraForm)
      });

      if (response.ok) {
        setSuccess(editingCamera ? t('configuracoes.camaraAtualizada') : t('configuracoes.camaraCriada'));
        setShowCameraForm(false);
        setCameraForm({ id: '', name: '', ip: '', stream: 'stream1', enabled: true });
        setEditingCamera(null);
        await loadCameras();
        await loadStats();
      } else {
        const data = await response.json();
        setError(data.detail || t('configuracoes.erroGuardarCamara'));
      }
    } catch (err) {
      setError(t('configuracoes.erroConexao'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCamera = async (cameraId) => {
    if (!window.confirm(t('configuracoes.confirmarDesativarCamara'))) return;

    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/config/cameras/${cameraId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        setSuccess(t('configuracoes.camaraDesativada'));
        await loadCameras();
        await loadStats();
      } else {
        setError(t('configuracoes.erroDesativarCamara'));
      }
    } catch (err) {
      setError(t('configuracoes.erroConexao'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCameraEnabled = async (camera) => {
    const updatedCamera = { ...camera, enabled: !camera.enabled };
    
    try {
      const response = await fetch(`${apiUrl}/config/cameras/${camera.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(updatedCamera)
      });

      if (response.ok) {
        await loadCameras();
        await loadStats();
      }
    } catch (err) {
      setError(t('configuracoes.erroAtualizarCamara'));
    }
  };

  // ==================== ESTORES - CRUD ====================

  const getNextShellyId = async () => {
    try {
      const response = await fetch(`${apiUrl}/config/shellys/next-id`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        return data.next_id;
      }
    } catch (err) {
      const maxId = shellys.reduce((max, s) => Math.max(max, s.id), 0);
      return maxId + 1;
    }
    return 1;
  };

  const handleNewShelly = async () => {
    const nextId = await getNextShellyId();
    setShellyForm({
      id: nextId,
      name: '',
      ip: '',
      room: '',
      type: 'cover',
      enabled: true
    });
    setEditingShelly(null);
    setShowShellyForm(true);
  };

  const handleEditShelly = (shelly) => {
    setShellyForm({
      id: shelly.id,
      name: shelly.name,
      ip: shelly.ip,
      room: shelly.room,
      type: shelly.type || 'cover',
      enabled: shelly.enabled
    });
    setEditingShelly(shelly.id);
    setShowShellyForm(true);
  };

  const handleSaveShelly = async () => {
    setError('');
    setSuccess('');

    if (!shellyForm.name || !shellyForm.ip || !shellyForm.room) {
      setError(t('configuracoes.nomeIpDivisaoObrigatorios'));
      return;
    }

    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(shellyForm.ip)) {
      setError(t('configuracoes.ipInvalido'));
      return;
    }

    setLoading(true);

    try {
      const url = editingShelly
        ? `${apiUrl}/config/shellys/${editingShelly}`
        : `${apiUrl}/config/shellys`;
      
      const method = editingShelly ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(shellyForm)
      });

      if (response.ok) {
        setSuccess(editingShelly ? t('configuracoes.estorAtualizado') : t('configuracoes.estorCriado'));
        setShowShellyForm(false);
        setShellyForm({ id: '', name: '', ip: '', room: '', type: 'cover', enabled: true });
        setEditingShelly(null);
        await loadShellys();
        await loadStats();
      } else {
        const data = await response.json();
        setError(data.detail || t('configuracoes.erroGuardarEstor'));
      }
    } catch (err) {
      setError(t('configuracoes.erroConexao'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShelly = async (shellyId) => {
    if (!window.confirm(t('configuracoes.confirmarDesativarEstor'))) return;

    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/config/shellys/${shellyId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        setSuccess(t('configuracoes.estorDesativado'));
        await loadShellys();
        await loadStats();
      } else {
        setError(t('configuracoes.erroDesativarEstor'));
      }
    } catch (err) {
      setError(t('configuracoes.erroConexao'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleShellyEnabled = async (shelly) => {
    const updatedShelly = { ...shelly, enabled: !shelly.enabled };
    
    try {
      const response = await fetch(`${apiUrl}/config/shellys/${shelly.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(updatedShelly)
      });

      if (response.ok) {
        await loadShellys();
        await loadStats();
      }
    } catch (err) {
      setError(t('configuracoes.erroAtualizarEstor'));
    }
  };

  // ==================== TESTAR CONEXÃO ====================

  const testConnection = async (ip, type) => {
    const key = `${type}-${ip}`;
    setTestingConnection(prev => ({ ...prev, [key]: 'testing' }));

    try {
      // Tentar fazer ping via backend
      const response = await fetch(`${apiUrl}/${type === 'camera' ? 'cameras' : 'shelly/devices'}`, { headers: authHeaders() });
      
      if (response.ok) {
        const data = await response.json();
        // Verificar se o dispositivo está online
        const devices = data.devices || data.cameras || data;
        const device = devices.find(d => d.ip === ip);
        
        if (device && device.online !== false) {
          setTestingConnection(prev => ({ ...prev, [key]: 'success' }));
        } else {
          setTestingConnection(prev => ({ ...prev, [key]: 'failed' }));
        }
      } else {
        setTestingConnection(prev => ({ ...prev, [key]: 'failed' }));
      }
    } catch (err) {
      setTestingConnection(prev => ({ ...prev, [key]: 'failed' }));
    }

    // Limpar após 3 segundos
    setTimeout(() => {
      setTestingConnection(prev => ({ ...prev, [key]: null }));
    }, 3000);
  };

  // ==================== LIMPAR MENSAGENS ====================

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ==================== RENDER ====================

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          Voltar
        </button>
        <div className="header-title">
          <SettingsIcon size={32} />
          <div>
            <h1>{t('configuracoes.titulo')}</h1>
            <p>{t('configuracoes.subtitulo')}</p>
          </div>
        </div>
        <button className="refresh-btn" onClick={loadAllData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="message error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="message success">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Estatísticas */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <Camera size={18} />
            <span>{stats.cameras.active} câmaras ativas</span>
            {stats.cameras.inactive > 0 && (
              <span className="inactive">({stats.cameras.inactive} inativas)</span>
            )}
          </div>
          <div className="stat-item">
            <Blinds size={18} />
            <span>{stats.shellys.active} estores ativos</span>
            {stats.shellys.inactive > 0 && (
              <span className="inactive">({stats.shellys.inactive} inativos)</span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'cameras' ? 'active' : ''}`}
          onClick={() => setActiveTab('cameras')}
        >
          <Camera size={20} />
          {t('configuracoes.tabCamaras')}
        </button>
        <button
          className={`tab ${activeTab === 'shellys' ? 'active' : ''}`}
          onClick={() => setActiveTab('shellys')}
        >
          <Blinds size={20} />
          {t('configuracoes.tabEstores')}
        </button>
      </div>

      {/* Conteúdo - Câmaras */}
      {activeTab === 'cameras' && (
        <div className="tab-content">
          <div className="content-header">
            <h2>{t('configuracoes.camarasTitulo')}</h2>
            <button className="btn-add" onClick={handleNewCamera}>
              <Plus size={18} />
              Adicionar Câmara
            </button>
          </div>

          {/* Formulário de Câmara */}
          {showCameraForm && (
            <div className="form-card">
              <h3>{editingCamera ? t('configuracoes.editarCamara') : t('configuracoes.novaCamara')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="number"
                    value={cameraForm.id}
                    onChange={(e) => setCameraForm({ ...cameraForm, id: parseInt(e.target.value) })}
                    disabled={editingCamera}
                  />
                </div>
                <div className="form-group">
                  <label>{t('configuracoes.nome')}</label>
                  <input
                    type="text"
                    value={cameraForm.name}
                    onChange={(e) => setCameraForm({ ...cameraForm, name: e.target.value })}
                    placeholder={t('configuracoes.placeholderNomeCamara')}
                  />
                </div>
                <div className="form-group">
                  <label>IP</label>
                  <input
                    type="text"
                    value={cameraForm.ip}
                    onChange={(e) => setCameraForm({ ...cameraForm, ip: e.target.value })}
                    placeholder={t('configuracoes.placeholderIpCamara')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('configuracoes.stream')}</label>
                  <select
                    value={cameraForm.stream}
                    onChange={(e) => setCameraForm({ ...cameraForm, stream: e.target.value })}
                  >
                    <option value="stream1">{t('configuracoes.stream1')}</option>
                    <option value="stream2">{t('configuracoes.stream2')}</option>
                  </select>
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={cameraForm.enabled}
                      onChange={(e) => setCameraForm({ ...cameraForm, enabled: e.target.checked })}
                    />
                    {t('configuracoes.ativa')}
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowCameraForm(false)}>
                  <X size={16} />
                  {t('comum.cancelar')}
                </button>
                <button className="btn-save" onClick={handleSaveCamera} disabled={loading}>
                  <Save size={16} />
                  {loading ? t('configuracoes.aGuardar') : t('comum.guardar')}
                </button>
              </div>
            </div>
          )}

          {/* Lista de Câmaras */}
          <div className="devices-list">
            {cameras.length === 0 ? (
              <div className="empty-state">
                <Camera size={48} />
                <p>{t('configuracoes.semCamaras')}</p>
                <button onClick={handleNewCamera}>{t('configuracoes.adicionarPrimeiraCamara')}</button>
              </div>
            ) : (
              cameras.map(camera => {
                const connectionKey = `camera-${camera.ip}`;
                const connectionStatus = testingConnection[connectionKey];
                
                return (
                  <div
                    key={camera.id}
                    className={`device-card ${!camera.enabled ? 'disabled' : ''}`}
                  >
                    <div className="device-icon">
                      <Camera size={24} />
                    </div>
                    <div className="device-info">
                      <h4>{camera.name}</h4>
                      <p className="device-details">
                        <span><Server size={14} /> IP: {camera.ip}</span>
                        <span>Stream: {camera.stream || 'stream1'}</span>
                      </p>
                      <div className="device-status">
                        {camera.enabled ? (
                          <span className="status online"><Wifi size={14} /> {t('configuracoes.ativa')}</span>
                        ) : (
                          <span className="status offline"><WifiOff size={14} /> {t('configuracoes.inativa')}</span>
                        )}
                        {connectionStatus === 'testing' && (
                          <span className="status testing"><RefreshCw size={14} className="spin" /> {t('configuracoes.aTestar')}</span>
                        )}
                        {connectionStatus === 'success' && (
                          <span className="status success"><CheckCircle size={14} /> {t('comum.online')}</span>
                        )}
                        {connectionStatus === 'failed' && (
                          <span className="status failed"><AlertCircle size={14} /> {t('comum.offline')}</span>
                        )}
                      </div>
                    </div>
                    <div className="device-actions">
                      <button
                        className="btn-icon"
                        onClick={() => testConnection(camera.ip, 'camera')}
                        title={t('configuracoes.testarConexao')}
                      >
                        <Wifi size={18} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleCameraEnabled(camera)}
                        title={camera.enabled ? t('configuracoes.desativar') : t('configuracoes.ativar')}
                      >
                        {camera.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleEditCamera(camera)}
                        title={t('configuracoes.editar')}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => handleDeleteCamera(camera.id)}
                        title={t('configuracoes.remover')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Conteúdo - Estores */}
      {activeTab === 'shellys' && (
        <div className="tab-content">
          <div className="content-header">
            <h2>{t('configuracoes.estoresTitulo')}</h2>
            <button className="btn-add" onClick={handleNewShelly}>
              <Plus size={18} />
              Adicionar Estor
            </button>
          </div>

          {/* Formulário de Estor */}
          {showShellyForm && (
            <div className="form-card">
              <h3>{editingShelly ? t('configuracoes.editarEstor') : t('configuracoes.novoEstor')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="number"
                    value={shellyForm.id}
                    onChange={(e) => setShellyForm({ ...shellyForm, id: parseInt(e.target.value) })}
                    disabled={editingShelly}
                  />
                </div>
                <div className="form-group">
                  <label>{t('configuracoes.nome')}</label>
                  <input
                    type="text"
                    value={shellyForm.name}
                    onChange={(e) => setShellyForm({ ...shellyForm, name: e.target.value })}
                    placeholder={t('configuracoes.placeholderNomeEstor')}
                  />
                </div>
                <div className="form-group">
                  <label>IP</label>
                  <input
                    type="text"
                    value={shellyForm.ip}
                    onChange={(e) => setShellyForm({ ...shellyForm, ip: e.target.value })}
                    placeholder={t('configuracoes.placeholderIpEstor')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('configuracoes.divisao')}</label>
                  <input
                    type="text"
                    value={shellyForm.room}
                    onChange={(e) => setShellyForm({ ...shellyForm, room: e.target.value })}
                    placeholder={t('configuracoes.placeholderDivisao')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('configuracoes.tipo')}</label>
                  <select
                    value={shellyForm.type}
                    onChange={(e) => setShellyForm({ ...shellyForm, type: e.target.value })}
                  >
                    <option value="cover">{t('configuracoes.tipoCover')}</option>
                    <option value="roller">{t('configuracoes.tipoRoller')}</option>
                  </select>
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={shellyForm.enabled}
                      onChange={(e) => setShellyForm({ ...shellyForm, enabled: e.target.checked })}
                    />
                    {t('configuracoes.ativo')}
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => setShowShellyForm(false)}>
                  <X size={16} />
                  {t('comum.cancelar')}
                </button>
                <button className="btn-save" onClick={handleSaveShelly} disabled={loading}>
                  <Save size={16} />
                  {loading ? t('configuracoes.aGuardar') : t('comum.guardar')}
                </button>
              </div>
            </div>
          )}

          {/* Lista de Estores */}
          <div className="devices-list">
            {shellys.length === 0 ? (
              <div className="empty-state">
                <Blinds size={48} />
                <p>{t('configuracoes.semEstores')}</p>
                <button onClick={handleNewShelly}>{t('configuracoes.adicionarPrimeiroEstor')}</button>
              </div>
            ) : (
              shellys.map(shelly => {
                const connectionKey = `shelly-${shelly.ip}`;
                const connectionStatus = testingConnection[connectionKey];
                
                return (
                  <div
                    key={shelly.id}
                    className={`device-card ${!shelly.enabled ? 'disabled' : ''}`}
                  >
                    <div className="device-icon">
                      <Blinds size={24} />
                    </div>
                    <div className="device-info">
                      <h4>{shelly.name}</h4>
                      <p className="device-details">
                        <span><Server size={14} /> IP: {shelly.ip}</span>
                        <span>Divisão: {shelly.room}</span>
                      </p>
                      <div className="device-status">
                        {shelly.enabled ? (
                          <span className="status online"><Wifi size={14} /> {t('configuracoes.ativo')}</span>
                        ) : (
                          <span className="status offline"><WifiOff size={14} /> {t('configuracoes.inativo')}</span>
                        )}
                        {connectionStatus === 'testing' && (
                          <span className="status testing"><RefreshCw size={14} className="spin" /> {t('configuracoes.aTestar')}</span>
                        )}
                        {connectionStatus === 'success' && (
                          <span className="status success"><CheckCircle size={14} /> {t('comum.online')}</span>
                        )}
                        {connectionStatus === 'failed' && (
                          <span className="status failed"><AlertCircle size={14} /> {t('comum.offline')}</span>
                        )}
                      </div>
                    </div>
                    <div className="device-actions">
                      <button
                        className="btn-icon"
                        onClick={() => testConnection(shelly.ip, 'shelly')}
                        title={t('configuracoes.testarConexao')}
                      >
                        <Wifi size={18} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleShellyEnabled(shelly)}
                        title={shelly.enabled ? t('configuracoes.desativar') : t('configuracoes.ativar')}
                      >
                        {shelly.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleEditShelly(shelly)}
                        title={t('configuracoes.editar')}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => handleDeleteShelly(shelly.id)}
                        title={t('configuracoes.remover')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
